import {useContainer} from '#imports';
import {DependencyInjectId} from '@@/package/const/DependencyInjectId';
import {AssessmentSummaryModel} from '~~/package/mapper/report/model/assessment/AssessmentSummaryModel';
import type ReportMapper from '~~/package/mapper/report/ReportMapper';
import {
    reportSampleAssessmentCandidateSummary,
    reportSampleAssessmentSummary, reportSampleCandidateActionAnalysis, reportSampleSkillCandidateSummaries,
    reportSampleSkillSummaries, reportSampleTrackCandidateSummaries,
    reportSampleTrackSummaries
} from '~~/package/mapper/report/sample/ReportSampleData';
import type {ReportDomainType} from '~~/package/mapper/report/model/ReportDomainType';
import {TrackSummaryModel} from '~~/package/mapper/report/model/assessment/TrackSummaryModel';
import {SkillSummaryModel} from '~~/package/mapper/report/model/assessment/SkillSummaryModel';
import {
    ProSkillManagerCandidateOrderType
} from '~~/package/mapper/proSkillManager/model/candidate/ProSkillManagerCandidateOrderType';
import {
    AssessmentCandidateSummaryModel
} from '~~/package/mapper/report/model/candidate/AssessmentCandidateSummaryModel';
import {TrackCandidateSummaryModel} from '~~/package/mapper/report/model/candidate/TrackCandidateSummaryModel';
import {CandidateActionAnalysisModel} from '~~/package/mapper/report/model/candidate/CandidateActionAnalysisModel';

export const useReportService = () => {
    const reportMapper = useContainer<ReportMapper>(DependencyInjectId.ReportMapper);

    const getReportAssessmentSummary = (domainType: ReportDomainType, domainId: number): Promise<AssessmentSummaryModel> => {
        if(domainId === 0) {
            return new Promise(resolve => {
                resolve(AssessmentSummaryModel.create(reportSampleAssessmentSummary));
            });
        } else {
            return reportMapper.getReportAssessmentSummary(domainType, domainId);
        }
    };

    const getReportTrackSummary = (domainType: ReportDomainType, domainId: number, sort?: string): Promise<TrackSummaryModel[]> => {
        if(domainId === 0) {
            return new Promise(resolve => { resolve(reportSampleTrackSummaries) });
        } else {
            return reportMapper.getReportTrackSummary(domainType, domainId, sort);
        }
    };

    const getReportSkillSummary = (lang: string, domainType: ReportDomainType, domainId: number, sort?: string): Promise<SkillSummaryModel[]> => {
        if(domainId === 0) {
            return new Promise(resolve => { resolve(reportSampleSkillSummaries) });
        } else {
            return reportMapper.getReportSkillSummary(lang, domainType, domainId, sort);
        }
    };

    const getReportCandidateAssessmentSummary = (userUUID: string, domainType: ReportDomainType, domainId: number): Promise<AssessmentCandidateSummaryModel> => {
        if(domainId === 0) {
            return new Promise(resolve => { resolve(AssessmentCandidateSummaryModel.create(reportSampleAssessmentCandidateSummary)) });
        } else {
            return reportMapper.getReportCandidateAssessmentSummary(userUUID, domainType, domainId);
        }
    };

    const getReportCandidateTrackSummary = (userUUID: string, domainType: ReportDomainType, domainId: number): Promise<TrackCandidateSummaryModel[]> => {
        if(domainId === 0) {
            return new Promise(resolve => { resolve(reportSampleTrackCandidateSummaries) });
        } else {
            return reportMapper.getReportCandidateTrackSummary(userUUID, domainType, domainId);
        }
    };

    const getReportCandidateSkillSummary = (lang: string, userUUID: string, domainType: ReportDomainType, domainId: number, sort?: string): Promise<SkillSummaryModel[]> => {
        if(domainId === 0) {
            return new Promise(resolve => { resolve(reportSampleSkillCandidateSummaries) });
        } else {
            return reportMapper.getReportCandidateSkillSummary(lang, userUUID, domainType, domainId, sort);
        }
    };

    const getReportCandidateActionAnalysis = (userUUID: string, domainType: ReportDomainType, domainId: number): Promise<CandidateActionAnalysisModel> => {
        if(domainId === 0) {
            return new Promise(resolve => {
                resolve(CandidateActionAnalysisModel.create(reportSampleCandidateActionAnalysis));
            });
        } else {
            return reportMapper.getReportCandidateActionAnalysis(userUUID, domainType, domainId);
        }
    };

    const submitReportExport = (email: string, url: string, domainType: ReportDomainType, domainId: number): Promise<boolean> => {
        return reportMapper.submitReportExport(email, url, domainType, domainId);
    };

    const submitBulkReportExport = (email: string, domainType: ReportDomainType, domainId: number): Promise<boolean> => {
        return reportMapper.submitBulkReportExport(email, domainType, domainId);
    };

    return {
        getReportAssessmentSummary,
        getReportTrackSummary,
        getReportSkillSummary,
        getReportCandidateAssessmentSummary,
        getReportCandidateTrackSummary,
        getReportCandidateSkillSummary,
        getReportCandidateActionAnalysis,
        submitReportExport,
        submitBulkReportExport
    };
}
